import { queryOptions, useQuery } from '@tanstack/react-query'
import {
  getPublicArticle,
  getPublicCollection,
  getPublicCollections,
  getPublicWorkspace,
  searchPublicArticles,
} from '../lib/public-kb-api'
import { getWorkspaceSlugFromHost } from '../lib/public-kb-host'

function publicKbEnabled() {
  return Boolean(getWorkspaceSlugFromHost())
}

export function publicWorkspaceQueryOptions() {
  const slug = getWorkspaceSlugFromHost()
  return queryOptions({
    queryKey: ['public-kb', slug, 'workspace'] as const,
    queryFn: async () => {
      const result = await getPublicWorkspace()
      if (!result.success) throw new Error(result.error.message)
      return result.data
    },
    enabled: publicKbEnabled(),
  })
}

export function publicCollectionsQueryOptions() {
  const slug = getWorkspaceSlugFromHost()
  return queryOptions({
    queryKey: ['public-kb', slug, 'collections'] as const,
    queryFn: async () => {
      const result = await getPublicCollections()
      if (!result.success) throw new Error(result.error.message)
      return result.data
    },
    enabled: publicKbEnabled(),
  })
}

export function publicCollectionQueryOptions(collectionSlug: string) {
  const slug = getWorkspaceSlugFromHost()
  return queryOptions({
    queryKey: ['public-kb', slug, 'collection', collectionSlug] as const,
    queryFn: async () => {
      const result = await getPublicCollection(collectionSlug)
      if (!result.success) throw new Error(result.error.message)
      return result.data
    },
    enabled: publicKbEnabled() && Boolean(collectionSlug),
  })
}

export function publicArticleQueryOptions(articleSlug: string) {
  const slug = getWorkspaceSlugFromHost()
  return queryOptions({
    queryKey: ['public-kb', slug, 'article', articleSlug] as const,
    queryFn: async () => {
      const result = await getPublicArticle(articleSlug)
      if (!result.success) throw new Error(result.error.message)
      return result.data
    },
    enabled: publicKbEnabled() && Boolean(articleSlug),
  })
}

export function publicSearchQueryOptions(query: string) {
  const slug = getWorkspaceSlugFromHost()
  return queryOptions({
    queryKey: ['public-kb', slug, 'search', query] as const,
    queryFn: async () => {
      const result = await searchPublicArticles(query)
      if (!result.success) throw new Error(result.error.message)
      return result.data
    },
    enabled: publicKbEnabled() && query.trim().length > 0,
  })
}

export function usePublicWorkspace() {
  return useQuery(publicWorkspaceQueryOptions())
}

export function usePublicCollections() {
  return useQuery(publicCollectionsQueryOptions())
}

export function usePublicCollection(collectionSlug: string) {
  return useQuery(publicCollectionQueryOptions(collectionSlug))
}

export function usePublicArticle(articleSlug: string) {
  return useQuery(publicArticleQueryOptions(articleSlug))
}

export function usePublicSearch(query: string) {
  return useQuery(publicSearchQueryOptions(query))
}
